import {
  ArchiveIcon,
  BellIcon,
  BriefcaseBusinessIcon,
  Building2Icon,
  BuildingIcon,
  CalendarDaysIcon,
  CalendarOffIcon,
  ChartNoAxesCombinedIcon,
  CircleUserIcon,
  ClockIcon,
  CreditCardIcon,
  FileClockIcon,
  FileSignatureIcon,
  FolderOpenIcon,
  GaugeIcon,
  GlobeIcon,
  HistoryIcon,
  HouseIcon,
  IdCardLanyardIcon,
  LayersIcon,
  LinkIcon,
  ListChecksIcon,
  NetworkIcon,
  ReceiptIcon,
  ReceiptTextIcon,
  SettingsIcon,
  SquareKanbanIcon,
  SquarePenIcon,
  StampIcon,
  TabletIcon,
  TargetIcon,
  UserCogIcon,
} from "lucide-react";
import type { ISidebar, SidebarSection } from "@/types/ISidebar";

enum Permission {
  Admin = "Admin",
  Comum = "Comum",
}

/** Ordem de exibição das seções do menu da empresa. */
export const SIDEBAR_SECTIONS: { key: SidebarSection; label: string }[] = [
  { key: "principal", label: "Principal" },
  { key: "comercial", label: "Comercial" },
  { key: "projetos", label: "Projetos" },
  { key: "financeiro", label: "Financeiro" },
  { key: "pessoas", label: "Pessoas" },
  { key: "administracao", label: "Administração" },
];

export const baseSidebar: ISidebar["sidebarData"] = [
  // --- Contexto GERAL (não dependem da empresa selecionada) ---
  {
    label: "Visão geral",
    path: "/visao-geral",
    icon: GaugeIcon,
    active: false,
    permission: [Permission.Admin, Permission.Comum],
    context: "geral",
  },
  {
    label: "Minha conta",
    path: "/perfil",
    icon: CircleUserIcon,
    active: false,
    permission: [Permission.Admin, Permission.Comum],
    context: "geral",
  },
  {
    label: "Notificações",
    path: "/notificacoes",
    icon: BellIcon,
    active: false,
    permission: [Permission.Admin, Permission.Comum],
    context: "geral",
  },
  {
    label: "Usuários",
    path: "/usuarios",
    icon: UserCogIcon,
    active: false,
    permission: [Permission.Admin],
    module: "users",
    context: "geral",
  },
  {
    label: "Plano",
    path: "/plano",
    icon: CreditCardIcon,
    active: false,
    permission: [Permission.Admin],
    ownerOnly: true,
    context: "geral",
  },
  {
    label: "Empresas",
    path: "/empresas",
    icon: BuildingIcon,
    active: false,
    permission: [],
    accountOwnerOrStaff: true,
    context: "geral",
  },
  {
    label: "Contas",
    path: "/contas",
    icon: LayersIcon,
    active: false,
    permission: [],
    superAdmin: true,
    context: "geral",
  },
  {
    label: "GeniaPay",
    path: "/plataforma/geniapay",
    icon: LinkIcon,
    active: false,
    permission: [],
    superAdmin: true,
    context: "geral",
  },
  // --- Contexto EMPRESA (dependem da empresa selecionada no header) ---
  {
    label: "Início",
    path: "/inicio",
    icon: HouseIcon,
    active: false,
    permission: [Permission.Admin, Permission.Comum],
    section: "principal",
    context: "empresa",
  },
  {
    label: "Meu dia",
    path: "/meu-dia",
    icon: ListChecksIcon,
    active: false,
    permission: [Permission.Admin, Permission.Comum],
    section: "principal",
    context: "empresa",
  },
  {
    label: "Meu ponto",
    path: "/meu-ponto",
    icon: StampIcon,
    active: false,
    permission: [Permission.Admin, Permission.Comum],
    module: "timeclock",
    section: "principal",
    context: "empresa",
  },
  {
    label: "Leads",
    path: "/leads",
    icon: TargetIcon,
    active: false,
    permission: [Permission.Admin],
    module: "leads",
    section: "comercial",
    context: "empresa",
  },
  {
    label: "Clientes",
    path: "/clientes",
    icon: Building2Icon,
    active: false,
    permission: [Permission.Admin],
    module: "clients",
    section: "comercial",
    context: "empresa",
  },
  {
    label: "Contatos",
    path: "/contatos",
    icon: UserCogIcon,
    active: false,
    permission: [Permission.Admin],
    module: "contacts",
    section: "comercial",
    context: "empresa",
  },
  {
    label: "Projetos",
    path: "/projetos",
    icon: ArchiveIcon,
    active: false,
    permission: [Permission.Admin],
    module: "projects",
    section: "projetos",
    context: "empresa",
  },
  {
    label: "Kanban",
    path: "/kanban",
    icon: SquareKanbanIcon,
    active: false,
    permission: [Permission.Admin, Permission.Comum],
    module: "kanban",
    section: "projetos",
    context: "empresa",
  },
  {
    label: "Documentos",
    path: "/documentos",
    icon: FolderOpenIcon,
    active: false,
    permission: [Permission.Admin, Permission.Comum],
    module: "documents",
    section: "projetos",
    context: "empresa",
  },
  {
    label: "Resumo",
    path: "/financeiro",
    icon: ChartNoAxesCombinedIcon,
    active: false,
    permission: [Permission.Admin],
    module: "finance",
    section: "financeiro",
    context: "empresa",
  },
  {
    label: "Faturas",
    path: "/faturas",
    icon: ReceiptTextIcon,
    active: false,
    permission: [Permission.Admin],
    module: "finance",
    section: "financeiro",
    context: "empresa",
  },
  {
    label: "Contratos",
    path: "/contratos-cobranca",
    icon: FileSignatureIcon,
    active: false,
    permission: [Permission.Admin],
    module: "finance",
    section: "financeiro",
    context: "empresa",
  },
  {
    label: "Assinantes",
    path: "/assinantes",
    icon: ReceiptIcon,
    active: false,
    permission: [Permission.Admin],
    module: "subscriptions",
    section: "financeiro",
    context: "empresa",
  },
  {
    label: "Departamentos",
    path: "/departamentos",
    icon: NetworkIcon,
    active: false,
    permission: [Permission.Admin],
    module: "departments",
    section: "pessoas",
    context: "empresa",
  },
  {
    label: "Funcionários",
    path: "/funcionarios",
    icon: IdCardLanyardIcon,
    active: false,
    permission: [Permission.Admin],
    module: "employees",
    section: "pessoas",
    context: "empresa",
  },
  {
    label: "Ponto",
    icon: BriefcaseBusinessIcon,
    active: false,
    description: "Marcações, ajustes, espelhos, jornadas e terminais.",
    permission: [Permission.Admin],
    module: "timeclock",
    section: "pessoas",
    context: "empresa",
    submenu: [
      { label: "Marcações", path: "/ponto", icon: StampIcon },
      { label: "Ajustes", path: "/ponto/ajustes", icon: SquarePenIcon },
      {
        label: "Justificativas",
        path: "/ponto/justificativas",
        icon: CalendarOffIcon,
      },
      { label: "Espelhos", path: "/ponto/espelhos", icon: FileClockIcon },
      { label: "Jornadas", path: "/jornadas", icon: ClockIcon },
      { label: "Feriados", path: "/feriados", icon: CalendarDaysIcon },
      { label: "Terminais", path: "/terminais", icon: TabletIcon },
      { label: "Terminal público", path: "/terminal-publico", icon: GlobeIcon },
    ],
  },
  {
    label: "Configurações",
    path: "/configuracoes",
    icon: SettingsIcon,
    active: false,
    permission: [Permission.Admin],
    section: "administracao",
    context: "empresa",
  },
  {
    label: "Auditoria",
    path: "/auditoria",
    icon: HistoryIcon,
    active: false,
    permission: [Permission.Admin],
    supportVisible: true,
    section: "administracao",
    context: "empresa",
  },
];
